import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Emergencymodal.css";

// Hook Redux
import useEmergencyContact from "../../Profile/hooks/useEmergencyContact"; 

const EmergencyModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    userId,
    contacts,
    status,
    error,
    handleRefresh,
    handleDelete,
    handleCall,
  } = useEmergencyContact();

  // ✅ Quand la modal s'ouvre, on refresh la liste
  useEffect(() => {
    if (!isOpen) return;
    if (!userId) return;
    handleRefresh();
  }, [isOpen, userId, handleRefresh]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleAddContact = () => {
    onClose();
    navigate("/profile/edit");
  };

  const isLoading = status === "loading";

  return (
    <div className="emergency-modal-backdrop" onClick={handleBackdropClick}>
      <div className="emergency-modal">
        {/* Header */}
        <div className="emergency-modal__header">
          <div className="emergency-modal__icon">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 10H6" />
              <path d="M14 18V6C14 5.46957 13.7893 4.96086 13.4142 4.58579C13.0391 4.21071 12.5304 4 12 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V17C2 17.2652 2.10536 17.5196 2.29289 17.7071C2.48043 17.8946 2.73478 18 3 18H5" />
              <path d="M19 18H21C21.2652 18 21.5196 17.8946 21.7071 17.7071C21.8946 17.5196 22 17.2652 22 17V13.72C21.9998 13.5102 21.9337 13.3058 21.8109 13.1356C21.6882 12.9655 21.515 12.8383 21.316 12.772L19.393 12.131C19.2684 12.0894 19.1532 12.0236 19.0541 11.9375C18.9549 11.8514 18.8737 11.7465 18.815 11.629L17.276 8.553C17.193 8.38692 17.0654 8.24722 16.9075 8.14955C16.7496 8.05188 16.5677 8.0001 16.382 8H14" />
              <path d="M8 8V12" />
              <path d="M9 18H15" />
              <path d="M17 20C18.1046 20 19 19.1046 19 18C19 16.8954 18.1046 16 17 16C15.8954 16 15 16.8954 15 18C15 19.1046 15.8954 20 17 20Z" />
              <path d="M7 20C8.10457 20 9 19.1046 9 18C9 16.8954 8.10457 16 7 16C5.89543 16 5 16.8954 5 18C5 19.1046 5.89543 20 7 20Z" />
            </svg>
          </div>

          <h2 className="emergency-modal__title">{t('emergencyModal.title')}</h2>

          <button
            className="emergency-modal__close"
            onClick={onClose}
            aria-label={t('emergencyModal.close')}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              stroke="currentColor"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="emergency-modal__divider" />

        {/* Content */}
        <div className="emergency-modal__content">
          {/* Loading */}
          {isLoading && (
            <div className="emergency-modal__empty">
              <p>{t('emergencyModal.loading')}</p>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className="emergency-modal__empty">
              <p style={{ color: "crimson" }}>{String(error)}</p>
              <button className="emergency-modal__add" onClick={handleRefresh}>
                {t('emergencyModal.retry')}
              </button>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && contacts.length === 0 && (
            <div className="emergency-modal__empty">
              <p>{t('emergencyModal.noContacts')}</p>
            </div>
          )}

          {/* List */}
          {!isLoading &&
            !error &&
            contacts.map((contact) => {
              const id = contact?.id ?? contact?._id;
              const name = contact?.name ?? t('emergencyModal.unknown');
              const relation = contact?.relation ?? "";
              const phone = contact?.phone ?? "";
              const avatar = contact?.avatar ?? null;

              return (
                <div key={id} className="emergency-contact">
                  {/* Avatar */}
                  <div className="emergency-contact__avatar">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={name}
                        className="emergency-contact__avatar-img"
                      />
                    ) : (
                      <div className="emergency-contact__avatar-placeholder">
                        {String(name).charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="emergency-contact__info">
                    <div className="emergency-contact__name">
                      {name}
                      {relation ? (
                        <span className="emergency-contact__relation">
                          {" "}
                          ( {relation} )
                        </span>
                      ) : null}
                    </div>

                    <div className="emergency-contact__phone">{phone}</div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {/* Call */}
                    <button
                      className="emergency-contact__call"
                      onClick={() => handleCall(phone)}
                      aria-label={t('emergencyModal.callAction', { name })}
                      title={t('emergencyModal.call')}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Footer */}
        <div className="emergency-modal__footer">
          <button className="emergency-modal__add" onClick={handleAddContact}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <circle cx="12" cy="12" r="10" />
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="16"
                stroke="white"
                strokeWidth="2"
              />
              <line
                x1="8"
                y1="12"
                x2="16"
                y2="12"
                stroke="white"
                strokeWidth="2"
              />
            </svg>
            <span>{t('emergencyModal.addNew')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyModal;